import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { MapContainer, TileLayer, ZoomControl, ScaleControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ProjectMetric {
  value: string;
  trend: 'up' | 'down';
  target: string;
}

interface ProjectTask {
  title: string;
  description?: string;
  status: 'completed' | 'in_progress' | 'planned';
  progress: number;
}

interface TeamMember {
  name: string;
  role: string;
  department: string;
}

interface ProjectRisk {
  description: string;
  severity: 'high' | 'medium' | 'low';
  mitigation: string;
}

interface ProjectDetails {
  facilities?: string;
  serviceLines?: string;
  metrics: {
    [key: string]: ProjectMetric;
  };
  tasks: ProjectTask[];
  team: TeamMember[];
  risks: ProjectRisk[];
}

interface Project {
  id: number;
  name: string;
  category: string;
  status: 'active' | 'planning';
  lastUpdated: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  facilities?: string;
  serviceLines?: string;
}

interface ProjectDetailsMap {
  [key: number]: ProjectDetails;
}

/**
 * Project Management Component
 * 
 * Provides interface for:
 * - Viewing and managing healthcare optimization projects
 * - Creating new projects
 * - Searching and filtering existing projects
 * - Detailed project analytics and metrics
 */
const Project: React.FC = () => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [openNewProject, setOpenNewProject] = useState(false);
  const [openProjectDetail, setOpenProjectDetail] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectType, setProjectType] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [tabValue, setTabValue] = useState(0);

  // Detailed project data
  const projectDetails: ProjectDetailsMap = {
    1: {
      facilities: 'FL126',
      serviceLines: 'Cardiology, Interventional Cardiology',
      metrics: {
        netInterventionalCardiologyPatientVolume: { value: '150 procedures/quarter', trend: 'down', target: 'Maintain > 145 procedures/quarter' },
        cardiacReferralLeakageRate: { value: '5% of primary care referrals', trend: 'down', target: 'Reduce to less than 3%' },
        serviceLineContributionMargin: { value: '28%', trend: 'up', target: 'Maintain greater than 25%' },
        irisCareScoreImpact: { value: '78', trend: 'up', target: 'Increase to greater than 82 within 9 months' }
      },
      tasks: [
        { 
          title: 'Phase 1: Quantify the Threat', 
          description: 'Use Iris\'s predictive model to simulate the competitor\'s patient capture curve and quantify the resulting patient leakage and associated revenue risk over 24 months.',
          status: 'in_progress', 
          progress: 75 
        },
        { 
          title: 'Phase 2: Model Counter-Strategy', 
          description: 'Design and simulate three distinct counter-strategies (e.g., price parity, new specialized service, provider recruitment) in the 3D sandbox to identify the highest-ROI move.',
          status: 'in_progress', 
          progress: 45 
        },
        { 
          title: 'Phase 3: Execute & Monitor', 
          description: 'Implement the chosen strategy, and use the live dashboard to continuously track patient retention, referral patterns, and Care Score changes in the affected zip codes.',
          status: 'planned', 
          progress: 0 
        }
      ],
      team: [
        { name: 'Sarah Chen', role: 'Revenue Cycle Director', department: 'Finance' },
        { name: 'Mike Rodriguez', role: 'Data Scientist', department: 'Analytics' },
        { name: 'Dr. James Wilson', role: 'Clinical Advisor', department: 'Medical' }
      ],
      risks: [
        { description: 'Competitor aggressive pricing strategy', severity: 'high', mitigation: 'Monitor competitor pricing and adjust strategy accordingly' },
        { description: 'Provider attrition risk', severity: 'medium', mitigation: 'Implement retention incentives and competitive compensation packages' }
      ]
    },
    2: {
      facilities: 'FL124',
      serviceLines: 'Orthopedics, Primary Care',
      metrics: {
        projectedVsActualPatientCapture: { value: '0 (pre-opening)', trend: 'up', target: 'Achieve greater than 90% of Iris\' predicted year 1 volume' },
        marketShareGain: { value: '12%', trend: 'up', target: 'Increase to greater than 18% within 18 months' },
        returnOnInvestment: { value: '-100% (negative before opening)', trend: 'up', target: 'Achieve greater than 25% within 3 years' },
        timeToBreakEven: { value: '0 (not open)', trend: 'down', target: 'Less than 24 months' }
      },
      tasks: [
        { 
          title: 'Phase 1: Site & Service Line Modeling', 
          description: 'Model the top three recommended sites and the optimal specialty mix (e.g., Primary Care, Ortho) for each to maximize patient capture and complement existing network assets.',
          status: 'completed', 
          progress: 100 
        },
        { 
          title: 'Phase 2: Scenario ROI Validation', 
          description: 'Run a financial simulation for the top-ranked scenario to confirm projected patient capture, competitive response, and 5-year Return on Investment (ROI) targets.',
          status: 'in_progress', 
          progress: 60 
        },
        { 
          title: 'Phase 3: Acquisition & Activation', 
          description: 'Secure real estate for the highest-ROI site and initiate the facility\'s build-out, provider recruitment, and payer contract integration plan.',
          status: 'planned', 
          progress: 15 
        }
      ],
      team: [
        { name: 'Dr. Emily Chang', role: 'Clinical Operations Director', department: 'Operations' },
        { name: 'Tom Stevens', role: 'Process Improvement Lead', department: 'Quality' },
        { name: 'Lisa Martinez', role: 'Nurse Manager', department: 'Nursing' }
      ],
      risks: [
        { description: 'Real estate acquisition delays', severity: 'high', mitigation: 'Engage multiple real estate brokers and maintain backup site options' },
        { description: 'Regulatory approval delays', severity: 'medium', mitigation: 'Early engagement with regulatory bodies and CON compliance' }
      ]
    },
    3: {
      facilities: 'Multi',
      serviceLines: 'Multi',
      metrics: {
        actualVsProjectedSynergies: { value: 'Projected Synergies: $15M', trend: 'up', target: 'Capture greater than 80% of projected synergies in Year 1' },
        postAcquisitionPatientVolumeRetention: { value: '100% (pre-close)', trend: 'down', target: 'Maintain greater than 95% retention after 12 months' },
        combinedRegionalCareScore: { value: 'Acquirer 80; Target: 70', trend: 'up', target: 'Combined entity greater than 77 within 18 months' },
        providerAttritionRate: { value: 'N/A', trend: 'down', target: 'less than 5% of key clinical providers in Year 1' }
      },
      tasks: [
        { 
          title: 'Phase 1: Synergies & Risk Assessment', 
          description: 'Utilize Iris to model financial, operational, and market synergies, identifying and quantifying key risks like payer contract dilution or provider attrition in the target\'s network.',
          status: 'in_progress', 
          progress: 80 
        },
        { 
          title: 'Phase 2: Integration Scenario Simulation', 
          description: 'Simulate the post-merger combined entity in the 3D sandbox, modeling different integration speeds (e.g., EHR, supply chain) and predicting the combined Care Score for the region.',
          status: 'in_progress', 
          progress: 50 
        },
        { 
          title: 'Phase 3: Valued-Based Negotiation & Plan Handoff', 
          description: 'Leverage the quantified strategic value from the simulations to inform the final acquisition price and generate a priority-ranked, post-close 100-day integration playbook.',
          status: 'planned', 
          progress: 25 
        }
      ],
      team: [
        { name: 'Dr. Emily Chang', role: 'Clinical Operations Director', department: 'Operations' },
        { name: 'Tom Stevens', role: 'Process Improvement Lead', department: 'Quality' },
        { name: 'Lisa Martinez', role: 'Nurse Manager', department: 'Nursing' }
      ],
      risks: [
        { description: 'Payer contract dilution', severity: 'high', mitigation: 'Comprehensive contract review and renegotiation strategy' },
        { description: 'Provider attrition in target network', severity: 'high', mitigation: 'Retention bonuses and cultural integration program' }
      ]
    }
  };

  // Project data
  const projects: Project[] = [
    {
      id: 1,
      name: 'Cardiac ASC Competitive Response',
      category: 'Competitive Response and Positioning',
      status: 'active',
      lastUpdated: '2024-03-31',
      description: 'Evaluate the impact of a new competitor Ambulatory Surgical Center (ASC) specializing in interventional cardiology and model a strategic counter-move to retain key patients.',
      priority: 'high',
      facilities: 'FL126',
      serviceLines: 'Cardiology, Interventional Cardiology',
    },
    {
      id: 2,
      name: 'Irvine Outpatient Network Expansion Q4',
      category: 'Market Expansion',
      status: 'active',
      lastUpdated: '2024-03-30',
      description: 'Identify the top three new market entry sites in the Irvine sub-market for a multi-specialty outpatient clinic to maximize patient capture and ROI.',
      priority: 'high',
      facilities: 'FL124',
      serviceLines: 'Orthopedics, Primary Care',
    },
    {
      id: 3,
      name: 'Acquisition Due Diligence: Community Care Network',
      category: 'M&A',
      status: 'active',
      lastUpdated: '2024-03-29',
      description: 'Conduct a full M&A scenario analysis of a potential acquisition target, modeling financial synergies, market share impact, and cultural integration risks.',
      priority: 'high',
      facilities: 'Multi',
      serviceLines: 'Multi',
    },
    {
      id: 4,
      name: 'Midland Hospital Underutilized Space Repurposing',
      category: 'Operations/Service Line Optimization',
      status: 'planning',
      lastUpdated: '2024-03-28',
      description: 'Model the conversion of 10,000 sq ft of declining inpatient space into a high-demand service line, ensuring Certificate of Need (CON) compliance and achieving a targeted revenue increase.',
      priority: 'medium',
    },
    {
      id: 5,
      name: 'Geriatric Care Score Improvement Initiative',
      category: 'Payor Network/Value-Based Care Optimization',
      status: 'active',
      lastUpdated: '2024-03-27',
      description: 'Launch a project to identify and address the facility and provider-level factors driving a low Care Score among the 65+ patient cohort to meet Value-Based Care metrics.',
      priority: 'high',
    },
    {
      id: 6,
      name: 'Cardiology Referral Leakage Audit Q3',
      category: 'Patient Leakage Reduction',
      status: 'active',
      lastUpdated: '2024-03-26',
      description: 'Analyze patient journeys to pinpoint specific out-migration/drop-off points and referring providers responsible for Cardiology service line leakage to competitor networks.',
      priority: 'medium',
    },
    {
      id: 7,
      name: 'Consolidation of North County Primary Care Clinics',
      category: 'Service/Site Relocation or Consolidation',
      status: 'planning',
      lastUpdated: '2024-03-25',
      description: 'Simulate the consolidation of two underperforming primary care facilities into one optimal site to improve operational efficiency and preserve patient volume across the combined service area.',
      priority: 'medium',
    },
    {
      id: 8,
      name: 'Q2 Targeted Marketing for Young Families Cohort',
      category: 'Patient Marketing',
      status: 'active',
      lastUpdated: '2024-03-24',
      description: 'Design and A/B test a targeted marketing campaign for the "Young Families" cohort to capture market share lost to a nearby Urgent Care center.',
      priority: 'low',
    },
    {
      id: 9,
      name: 'Optimal Staffing Model: New Ortho-Spine Program',
      category: 'Hiring/Recruitment',
      status: 'planning',
      lastUpdated: '2024-03-23',
      description: 'Determine the ideal FTE mix (Physicians, PAs, NPs) and schedule utilization targets required to launch and sustain a high-performing Orthopedic-Spine program.',
      priority: 'medium',
    },
    {
      id: 10,
      name: 'Imaging Services Hub-and-Spoke Redesign',
      category: 'Operations/Service Line Optimization',
      status: 'active',
      lastUpdated: '2024-03-22',
      description: 'Evaluate a hybrid consolidation strategy for imaging services, establishing a central advanced imaging hub while maintaining essential basic capabilities at all hospital campuses.',
      priority: 'medium',
    },
    {
      id: 11,
      name: 'EHR Integration and Care Coordination Improvement',
      category: 'Comprehensive Care Delivery Strategy',
      status: 'active',
      lastUpdated: '2024-03-21',
      description: 'Identify and quantify the impact of poor EHR integration and care transitions on hospital readmission rates for the high-risk Chronic Disease cohort.',
      priority: 'high',
    },
    {
      id: 12,
      name: 'Payor Mix Analysis for New Risk Contract Negotiation',
      category: 'Payor Network/Value-Based Care Optimization',
      status: 'planning',
      lastUpdated: '2024-03-20',
      description: 'Model the patient volume, revenue, and Care Score impact of joining a new Value-Based Care/risk contract in the market, advising on optimal negotiation parameters.',
      priority: 'medium',
    },
  ];

  const handleCreateProject = () => {
    // To be implemented with actual database integration
    setOpenNewProject(false);
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setOpenProjectDetail(true);
    setTabValue(0);
  };

  const handleCloseProjectDetail = () => {
    setOpenProjectDetail(false);
    setSelectedProject(null);
  };

  const getMetricColor = (trend: string) => {
    return trend === 'up' ? 'success.main' : 'error.main';
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success.main';
      case 'in_progress':
        return 'info.main';
      default:
        return 'text.secondary';
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || project.category === filterCategory;
    const matchesPriority = filterPriority === 'all' || project.priority === filterPriority;
    return matchesSearch && matchesCategory && matchesPriority;
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Healthcare Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenNewProject(true)}
        >
          New Project
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={filterCategory}
                label="Filter by Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="Competitive Response and Positioning">Competitive Response and Positioning</MenuItem>
                <MenuItem value="Market Expansion">Market Expansion</MenuItem>
                <MenuItem value="M&A">M&A</MenuItem>
                <MenuItem value="Operations/Service Line Optimization">Operations/Service Line Optimization</MenuItem>
                <MenuItem value="Payor Network/Value-Based Care Optimization">Payor Network/Value-Based Care Optimization</MenuItem>
                <MenuItem value="Patient Leakage Reduction">Patient Leakage Reduction</MenuItem>
                <MenuItem value="Service/Site Relocation or Consolidation">Service/Site Relocation or Consolidation</MenuItem>
                <MenuItem value="Patient Marketing">Patient Marketing</MenuItem>
                <MenuItem value="Hiring/Recruitment">Hiring/Recruitment</MenuItem>
                <MenuItem value="Comprehensive Care Delivery Strategy">Comprehensive Care Delivery Strategy</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Priority</InputLabel>
              <Select
                value={filterPriority}
                label="Filter by Priority"
                onChange={(e) => setFilterPriority(e.target.value)}
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Projects List */}
      <Grid container spacing={3}>
        {filteredProjects.map((project) => (
          <Grid item xs={12} md={6} key={project.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { boxShadow: 6 }
              }}
              onClick={() => handleProjectClick(project)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" gutterBottom sx={{ flex: 1 }}>
                    {project.name}
                  </Typography>
                  <Chip
                    label={project.priority}
                    color={project.priority === 'high' ? 'error' : project.priority === 'medium' ? 'warning' : 'default'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {project.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={project.status}
                      color={project.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                    <Chip
                      label={project.category}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Last updated: {project.lastUpdated}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Project Detail Dialog */}
      <Dialog 
        open={openProjectDetail} 
        onClose={handleCloseProjectDetail}
        maxWidth="md"
        fullWidth
      >
        {selectedProject && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {selectedProject.name}
                <Chip
                  label={selectedProject.status}
                  color={selectedProject.status === 'active' ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab label="Overview" icon={<AssignmentIcon />} iconPosition="start" />
                <Tab label="Metrics" icon={<TimelineIcon />} iconPosition="start" />
                <Tab label="Team" icon={<PeopleIcon />} iconPosition="start" />
                <Tab label="Risks" icon={<WarningIcon />} iconPosition="start" />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                {/* Facilities and Service Lines - only for first 3 projects */}
                {projectDetails[selectedProject.id]?.facilities && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Facility(s) in Scope: <strong>{projectDetails[selectedProject.id].facilities}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Service Lines/Specialties in Scope: <strong>{projectDetails[selectedProject.id].serviceLines}</strong>
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                  </>
                )}
                
                <Typography variant="body1" paragraph>
                  {selectedProject.description}
                </Typography>
                
                {projectDetails[selectedProject.id] && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Tasks</Typography>
                    <List>
                      {projectDetails[selectedProject.id].tasks.map((task: ProjectTask, index: number) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: getTaskStatusColor(task.status) }} />
                          </ListItemIcon>
                        <ListItemText
                          primary={task.title}
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {task.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {task.description}
                                </Typography>
                              )}
                              <LinearProgress 
                                variant="determinate" 
                                value={task.progress} 
                                sx={{ mb: 1 }}
                              />
                              <Typography variant="caption">
                                Progress: {task.progress}%
                              </Typography>
                            </Box>
                          }
                        />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Embedded Map - only for first 3 projects */}
                {projectDetails[selectedProject.id]?.facilities && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Location</Typography>
                    <Box sx={{ height: 300, width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                      <MapContainer 
                        center={[28.5383, -81.3792]} 
                        zoom={10} 
                        style={{ height: '100%', width: '100%' }}
                        zoomControl={false}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <ZoomControl position="bottomright" />
                        <ScaleControl position="bottomleft" />
                      </MapContainer>
                    </Box>
                  </Box>
                )}

                {/* Generate Business Case Button - only for first 3 projects */}
                {projectDetails[selectedProject.id]?.facilities && (
                  <Box sx={{ mt: 2 }}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                        }
                      }}
                    >
                      Generate Business Case
                    </Button>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                {projectDetails[selectedProject.id] ? (
                  <Grid container spacing={3}>
                    {Object.entries(projectDetails[selectedProject.id].metrics).map(([key, metric]: [string, ProjectMetric]) => {
                      // Format metric name for display
                      const formattedName = key
                        .replace(/([A-Z])/g, ' $1')
                        .trim()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ');
                      
                      return (
                        <Grid item xs={12} sm={6} key={key}>
                          <Card>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {formattedName}
                              </Typography>
                              <Box sx={{ mt: 1, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Current: <strong>{metric.value}</strong>
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <TrendingUpIcon 
                                    sx={{ 
                                      mr: 0.5,
                                      fontSize: '1rem',
                                      color: getMetricColor(metric.trend),
                                      transform: metric.trend === 'down' ? 'rotate(180deg)' : 'none'
                                    }}
                                  />
                                </Box>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Target: <strong>{metric.target}</strong>
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Metrics not available for this project.
                  </Typography>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                {projectDetails[selectedProject.id] ? (
                  <List>
                    {projectDetails[selectedProject.id].team.map((member: TeamMember, index: number) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemIcon>
                          <PeopleIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={member.name}
                          secondary={`${member.role} - ${member.department}`}
                        />
                      </ListItem>
                      {index < projectDetails[selectedProject.id].team.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Team information not available for this project.
                  </Typography>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                {projectDetails[selectedProject.id] ? (
                  <List>
                    {projectDetails[selectedProject.id].risks.map((risk: ProjectRisk, index: number) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <WarningIcon color={risk.severity === 'high' ? 'error' : 'warning'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={risk.description}
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              Severity: {risk.severity}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Mitigation: {risk.mitigation}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Risk information not available for this project.
                  </Typography>
                )}
              </TabPanel>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseProjectDetail}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* New Project Dialog */}
      <Dialog open={openNewProject} onClose={() => setOpenNewProject(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Healthcare Project</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Project Description"
              variant="outlined"
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Project Type</InputLabel>
              <Select
                value={projectType}
                label="Project Type"
                onChange={(e) => setProjectType(e.target.value)}
              >
                <MenuItem value="workflow">Workflow Optimization</MenuItem>
                <MenuItem value="resource">Resource Management</MenuItem>
                <MenuItem value="quality">Quality Improvement</MenuItem>
                <MenuItem value="analytics">Healthcare Analytics</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewProject(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateProject}>Create Project</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Project; 